/**
 * useMetadataForm Hook
 *
 * Purpose: Custom hook for metadata form state management and validation
 * Architecture: Manages form state, fetches view configuration, handles validation
 * Dependencies: React, metadata service, react-query
 * Author: Development Team
 * Created: 2025-01-31
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { asList, SINGLE_SELECT_ERROR } from '../../utils/listValues';
import { useQuery } from '@tanstack/react-query';
import { metadataService } from '../../services/metadataService';
import { metadataViewQueryKey } from '../../hooks/useModelFieldLabels';
import { usePickerFilterCheck } from '../../hooks/usePickerFilterCheck';
import type { PermissionKey } from '../../constants/permissionKeys';
import type { FormData, ViewField, ViewConfiguration, UseMetadataFormReturn } from './types';

/** ORCH-400: what the server-side value check needs to know about the form */
export interface MetadataFormCheckOptions {
  pickerPermission?: PermissionKey | null;
  /** The record an edit form opened (its stored values decide what is locked) */
  recordId?: string | null;
  readOnly?: boolean;
}

/**
 * Custom hook for metadata form management
 *
 * Purpose: Provides form state management, validation, and view configuration fetching
 * Logic: Fetches view config from API, manages form state, validates fields
 * Input:
 *   - modelId: Model identifier for fetching view configuration
 *   - tenantId: Tenant context for field resolution
 *   - initialData: Optional initial form data for edit mode
 *   - onChange: Optional callback for form data changes
 *   - providedViewConfig: Optional pre-fetched view configuration
 *   - fixedFields: Optional fixed fields for validation
 * Output: Form state and actions for managing the form
 * Exceptions: None - errors returned in error state
 */
export const useMetadataForm = (
  modelId: string,
  tenantId: string,
  initialData?: Record<string, any>,
  onChange?: (currentData: FormData, initialData: FormData) => void,
  providedViewConfig?: ViewConfiguration,
  fixedFields?: Array<{ fieldId: string; label: string; required?: boolean }>,
  checkOptions?: MetadataFormCheckOptions
): UseMetadataFormReturn => {
  // Fetch view configuration (skip if viewConfig is provided)
  const {
    data: fetchedViewConfig,
    isLoading: loading,
    error: queryError,
  } = useQuery<ViewConfiguration>({
    queryKey: metadataViewQueryKey(modelId, 'edit-modal', tenantId),
    queryFn: async () => {
      // Use getModelView which calls GET /metadata/models/{model_id}/view
      // This endpoint returns the configured view or generates a default view
      const response = await metadataService.getModelView(modelId, tenantId, 'edit-modal');
      return response as unknown as ViewConfiguration;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!modelId && !!tenantId && !providedViewConfig,
  });

  // Use provided viewConfig if available, otherwise use fetched config
  const viewConfig = providedViewConfig || fetchedViewConfig;

  // Initialize form data with initial data or empty state
  const initialFormData: FormData = {
    ...initialData,
    metadata: initialData?.metadata || {},
  };

  // Form state
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [ownErrors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouchedFields] = useState<Record<string, boolean>>({});

  // ORCH-400 §4.6: the server's verdict on the selected list values, kept apart from the
  // form's own errors; it clears only when the next answer says the field matches
  const listFieldIds = useMemo(
    () =>
      (viewConfig?.fields ?? [])
        .filter((f: ViewField) => f.field_definition?.datatype === 'list')
        .map((f: ViewField) => f.fielddef_id),
    [viewConfig]
  );
  const { errors: filterErrors, locked: pickerLocked } = usePickerFilterCheck({
    modelId,
    tenantId,
    values: formData.metadata,
    listFieldIds,
    permission: checkOptions?.pickerPermission ?? null,
    recordId: checkOptions?.recordId ?? null,
    enabled: !checkOptions?.readOnly,
  });
  const filterErrorsRef = useRef(filterErrors);
  useEffect(() => {
    filterErrorsRef.current = filterErrors;
    const ids = Object.keys(filterErrors);
    if (ids.length) {
      setTouchedFields((prev) => {
        if (ids.every((id) => prev[id])) return prev;
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
    }
  }, [filterErrors]);
  const errors = useMemo(() => ({ ...filterErrors, ...ownErrors }), [filterErrors, ownErrors]);

  // Update form data when initial data changes
  useEffect(() => {
    const newFormData: FormData = {
      ...initialData,
      metadata: initialData?.metadata || {},
    };
    setFormData(newFormData);
  }, [initialData]);

  // Call onChange callback when form data changes
  useEffect(() => {
    if (onChange) {
      onChange(formData, initialFormData);
    }
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Set value for fixed field
   *
   * Purpose: Update fixed field value in form state
   * Input: fieldId - field identifier, value - new value
   */
  const setValue = useCallback((fieldId: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  /**
   * Set value for metadata field
   *
   * Purpose: Update metadata field value using fielddef_id as key
   * Logic: Always uses fielddef_id as the key (not field_id)
   * Input: fieldDefId - field definition identifier, value - new value
   */
  const setMetadataValue = useCallback((fieldDefId: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [fieldDefId]: value,
      },
    }));
  }, []);

  /**
   * Mark field as touched
   *
   * Purpose: Track which fields have been interacted with for validation display
   * Input: fieldId - field identifier to mark as touched
   */
  const setTouched = useCallback((fieldId: string) => {
    setTouchedFields((prev) => ({ ...prev, [fieldId]: true }));
  }, []);

  /**
   * Validate individual field
   *
   * Purpose: Validate field value against field definition rules
   * Logic: Checks required, datatype, and format validation
   * Input: field - view field configuration, value - current value
   * Output: Error message string or null if valid
   */
  const validateField = useCallback((field: ViewField, value: any): string | null => {
    // Required validation - trim whitespace for string values before checking if empty
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    if (
      field.is_required &&
      (trimmedValue === null || trimmedValue === undefined || trimmedValue === '')
    ) {
      return `${field.label_override || field.field_definition.label} is required`;
    }

    // Skip further validation if value is empty and not required
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Data type validation
    const datatype = field.field_definition.datatype;
    switch (datatype.toLowerCase()) {
      case 'currency':
      case 'decimal':
      case 'number':
        if (isNaN(value)) {
          return 'Must be a valid number';
        }
        break;
      case 'date':
      case 'datetime':
        if (!(value instanceof Date) && isNaN(Date.parse(value))) {
          return 'Must be a valid date';
        }
        break;
      case 'list':
        // Either shape is accepted and the server stores the declared one (ORCH-354);
        // only a single-select field holding several values is an error
        if (!field.field_definition.is_multiselect && asList(value).length > 1) {
          return SINGLE_SELECT_ERROR;
        }
        break;
      case 'bool':
      case 'boolean':
        if (typeof value !== 'boolean') {
          return 'Must be true or false';
        }
        break;
    }

    return null;
  }, []);

  /**
   * Validate entire form
   *
   * Purpose: Validate all fields (both fixed and metadata) and update error state
   * Logic: Validates fixed fields first, then metadata fields from view config
   * Output: Object with isValid boolean and errors record
   */
  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};
    const newTouched: Record<string, boolean> = {};

    // Validate fixed fields
    if (fixedFields) {
      fixedFields.forEach((fixedField) => {
        if (fixedField.required) {
          const value = formData[fixedField.fieldId];
          // Trim whitespace for string values before checking if empty
          const trimmedValue = typeof value === 'string' ? value.trim() : value;
          if (trimmedValue === null || trimmedValue === undefined || trimmedValue === '') {
            newErrors[fixedField.fieldId] = `${fixedField.label} is required`;
            newTouched[fixedField.fieldId] = true;
          }
        }
      });
    }

    // Validate metadata fields
    if (viewConfig) {
      viewConfig.fields.forEach((field: ViewField) => {
        const value = formData.metadata[field.fielddef_id];
        const error = validateField(field, value);
        if (error) {
          newErrors[field.fielddef_id] = error;
          newTouched[field.fielddef_id] = true;
        }
      });
    }

    // Update state with errors and mark invalid fields as touched
    setErrors(newErrors);
    setTouchedFields((prev) => ({ ...prev, ...newTouched }));

    // ORCH-400: a value the server would refuse blocks the save like a missing required field
    const combined = { ...filterErrorsRef.current, ...newErrors };
    return {
      isValid: Object.keys(combined).length === 0,
      errors: combined,
    };
  }, [viewConfig, formData, validateField, fixedFields]);

  /**
   * Reset form to initial state
   *
   * Purpose: Clear form data, errors, and touched state
   */
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setTouchedFields({});
  }, [initialFormData]);

  return {
    viewConfig: viewConfig || null,
    loading,
    error: queryError ? String(queryError) : null,
    formData,
    errors,
    touched,
    setValue,
    setMetadataValue,
    setTouched,
    validateField,
    validateForm,
    resetForm,
    pickerLocked,
  };
};
